import React, { useCallback } from 'react';
import { detect } from 'detect-browser';
import SwiperCore, { Autoplay, Keyboard, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper } from 'swiper/react';
import { useDeviceSelectors, isMobile, isTablet } from 'react-device-detect';

/* eslint-disable max-len */
import { SwiperSlide } from '@24i/nxg-sdk-gluons/src/components/containers/SingleRow/components/SingleRowSwiperSlide';
import { overridable } from '@24i/nxg-sdk-gluons/src/context/ComponentOverrides';
import { useTheme } from '@24i/nxg-sdk-higgs';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';
import { View } from '@24i/nxg-sdk-quarks';
import { useDimensions } from '@24i/nxg-sdk-quantum';
import { Asset, ASSET_TYPE } from '@24i/nxg-sdk-photon';
import { Breakpoint } from '../../../utils/styles/constants';

import Item from '../components/Item';
import getSliderStyles from '../styles';
import { getSliderTestId, SLIDER_TEST_IDS } from '../test-utils';
import { SliderWebComponentProps } from '../types';

SwiperCore.use([Mousewheel, Navigation, Pagination, Keyboard, Autoplay]);

const Slider = (props: SliderWebComponentProps) => {
    const {
        styles: getStyles = getSliderStyles,
        items: unlimitedItems = [],
        additionalItems,
        itemLimit,
        onItemPress,
        swiperProps,
        index: sliderIndex,
        showMetadata,
    } = props;

    const { theme } = useTheme();
    const styles = getStyles(theme);
    const { orientation, width } = useDimensions();
    // Use device selectors that update with window resize
    const [selectors] = useDeviceSelectors(navigator?.userAgent);

    const carouselConfig = useFeature('carousel');
    const autoSlideTimer = (carouselConfig?.slideTime ?? 3) * 1000; // Defaults to 3 seconds
    const generateSlides = () => {
        const items = unlimitedItems?.slice(0, itemLimit) || [];
        if (additionalItems) {
            additionalItems
                .slice()
                .reverse()
                .forEach(
                    (
                        {
                            id,
                            textPlacement,
                            position,
                            images,
                            target,
                            title,
                            description,
                            type,
                            background,
                        },
                        i
                    ) => {
                        const item: Asset = {
                            id: id || `${i}`,
                            title,
                            description,
                            background: background || images?.[0].url,
                            textPlacement,
                            type: type as ASSET_TYPE,
                            target,
                            images: images && { still: [images?.[0]] },
                        };
                        if (position === 'start') items.unshift(item);
                        if (position === 'end') items.push(item);
                    }
                );
        }

        return items.map((item, index) => (
            <SwiperSlide
                // @ts-ignore
                testid={getSliderTestId(SLIDER_TEST_IDS.CONTAINER, {
                    index,
                    scopeIndex: sliderIndex,
                })}
                key={item?.id || index}
            >
                <Item
                    index={index}
                    sliderIndex={sliderIndex}
                    key={item.id}
                    data={item}
                    onPress={() => onItemPress(item)}
                    styles={styles}
                    showMetadata={showMetadata}
                />
            </SwiperSlide>
        ));
    };

    const enableMouseWheel = detect(navigator?.userAgent)?.name !== 'safari';

    const renderSliderButtons = useCallback(() => {
        // Using selectors.isMobile from useDeviceSelectors which will update on window resize
        // Also check current width against Breakpoint.MD for responsiveness
        // The navigation arrows should only appear for desktop (not mobiles and tablets) and only for the specific row that currently has focus (hover)
        const isNotDesktop =
            (width <= Breakpoint.MD || selectors.isMobile) && (isTablet || isMobile);

        if (isNotDesktop) {
            return null;
        }

        return (
            <>
                <div
                    // @ts-ignore
                    testid={getSliderTestId(SLIDER_TEST_IDS.NEXT_BUTTON, {
                        scopeIndex: sliderIndex,
                    })}
                    style={styles.sliderNextButton}
                    className="slider-swiper-button swiper-button-next swiper-button-white swiper-button-small"
                />
                <div
                    // @ts-ignore
                    testid={getSliderTestId(SLIDER_TEST_IDS.PREV_BUTTON, {
                        scopeIndex: sliderIndex,
                    })}
                    style={styles.sliderPrevButton}
                    className="slider-swiper-button swiper-button-prev swiper-button-white swiper-button-small"
                />
            </>
        );
    }, [sliderIndex, styles, selectors.isMobile, getSliderTestId, SLIDER_TEST_IDS, width]);

    const items = generateSlides();
    return (
        <View
            style={styles.slideHolder}
            testID={getSliderTestId(SLIDER_TEST_IDS.CONTAINER, { scopeIndex: sliderIndex })}
        >
            <div className="slider-container">
                <style jsx>{`
                    :global(.slider-container) {
                        position: relative;
                        padding: 0 4%;
                    }
                    @media (max-width: ${Breakpoint.SM - 1}px) {
                        :global(.slider-container) {
                            padding: 0 20px;
                        }
                    }
                    @media (min-width: ${Breakpoint.SM}px) and (max-width: ${Breakpoint.L}px) {
                        :global(.slider-container) {
                            padding: 0 32px;
                        }
                    }
                    @media (min-width: ${Breakpoint.L + 1}px) and (max-width: ${Breakpoint.XL}px) {
                        :global(.slider-container) {
                            padding: 0 48px;
                        }
                    }
                    @media (min-width: ${Breakpoint.XL +
                        1}px) and (max-width: ${Breakpoint.XXL}px) {
                        :global(.slider-container) {
                            padding: 0 64px;
                        }
                    }
                    :global(.slider-container .swiper-container) {
                        overflow: visible;
                    }
                    :global(.slider-swiper-button.swiper-button-prev.swiper-button-disabled),
                    :global(.slider-swiper-button.swiper-button-next.swiper-button-disabled),
                    :global(.slider-swiper-button) {
                        top: 12px !important;
                        display: none !important;
                    }
                    :global(.slider-container:hover .slider-swiper-button) {
                        display: flex !important;
                        height: calc(100% - 12px);
                        width: 48px;
                    }
                    :global(.slider-container .swiper-button-next) {
                        position: absolute;
                        right: 0;
                        opacity: 0.6;
                        background: linear-gradient(
                            to right,
                            rgba(0, 0, 0, 0.2),
                            rgba(0, 0, 0, 0.7) 10%,
                            rgba(0, 0, 0, 0.8) 100%,
                            rgba(0, 0, 0, 0.2)
                        );
                    }
                    :global(.slider-container .swiper-button-prev) {
                        position: absolute;
                        left: 0;
                        opacity: 0.6;
                        background: linear-gradient(
                            to left,
                            rgba(0, 0, 0, 0.2),
                            rgba(0, 0, 0, 0.7) 10%,
                            rgba(0, 0, 0, 0.8) 100%,
                            rgba(0, 0, 0, 0.2)
                        );
                    }
                    :global(.swiper-button-next:hover),
                    :global(.swiper-button-prev:hover) {
                        opacity: 1;
                        tansition: opacity 0.1s easy-out 0s;
                    }
                    :global(.swiper-button-next::after),
                    :global(.swiper-button-prev::after) {
                        font-size: 32px !important;
                    }
                    :global(.swiper-button-next:hover.swiper-button-next::after),
                    :global(.swiper-button-prev:hover.swiper-button-prev::after) {
                        font-size: 36px !important;
                    }
                `}</style>
                <Swiper
                    autoplay={
                        carouselConfig?.autoSlide
                            ? {
                                  delay: autoSlideTimer,
                                  disableOnInteraction: false,
                              }
                            : false
                    }
                    key={orientation}
                    slidesPerView={1.16}
                    spaceBetween={8}
                    centeredSlides={false}
                    slidesOffsetBefore={4}
                    slidesPerGroup={1}
                    normalizeSlideIndex={false}
                    // Swiper applies the config for the largest matching breakpoint key <= current width.
                    // You cannot specify explicit ranges, only min-width keys. The +1 ensures no overlap at exact values.
                    // Always order breakpoints from smallest to largest for clarity.
                    // Use Breakpoint.SM for the smallest breakpoint as per design system
                    breakpoints={{
                        [Breakpoint.SM]: {
                            slidesPerView: 1.16,
                            spaceBetween: 8,
                            slidesOffsetBefore: 4,
                        },
                        [Breakpoint.XL + 1]: {
                            slidesPerView: 1.37,
                            spaceBetween: 10,
                            slidesOffsetBefore: 5,
                        },
                        [Breakpoint.XXL]: {
                            slidesPerView: 1.37,
                            spaceBetween: 16,
                            slidesOffsetBefore: 8,
                        },
                    }}
                    preloadImages={false}
                    lazy
                    grabCursor
                    threshold={15}
                    mousewheel={
                        enableMouseWheel
                            ? {
                                  forceToAxis: true,
                                  sensitivity: 0.7,
                                  thresholdDelta: 40,
                              }
                            : false
                    }
                    loop
                    slidesOffsetAfter={32}
                    watchOverflow
                    watchSlidesProgress
                    observer
                    observeParents
                    updateOnWindowResize
                    navigation={{
                        nextEl: `.slider-swiper-button.swiper-button-next`,
                        prevEl: `.slider-swiper-button.swiper-button-prev`,
                    }}
                    {...swiperProps}
                >
                    {items}
                </Swiper>
                {renderSliderButtons()}
            </div>
        </View>
    );
};

export { getSliderStyles };
export default overridable(Slider, 'Slider');
